import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  Skeleton,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Refresh,
  Warning,
  Inventory2,
  SwapHoriz,
  Add,
  Remove,
  TrendingDown,
  Schedule,
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

interface InventoryItem {
  id: string;
  itemCode: string;
  name: string;
  description?: string;
  category?: string;
  unit: string;
  quantityOnHand: number;
  reorderPoint: number;
  reorderQuantity: number;
  unitCost: number;
  expiryDate?: string;
  lotNumber?: string;
  location?: string;
}

interface InventoryTransaction {
  id: string;
  transactionType: string;
  quantity: number;
  unitCost: number;
  referenceType?: string;
  referenceId?: string;
  notes?: string;
  createdAt: string;
  userId: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function InventoryWorkflows() {
  const [tab, setTab] = useState(0);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [lowStock, setLowStock] = useState<InventoryItem[]>([]);
  const [expiringSoon, setExpiringSoon] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Transaction dialog
  const [txDialogOpen, setTxDialogOpen] = useState(false);
  const [txType, setTxType] = useState<'receive' | 'issue' | 'adjust'>('receive');
  const [txItem, setTxItem] = useState<InventoryItem | null>(null);
  const [txQuantity, setTxQuantity] = useState('');
  const [txUnitCost, setTxUnitCost] = useState('');
  const [txReason, setTxReason] = useState('');
  const [txSubmitting, setTxSubmitting] = useState(false);

  // Transaction history
  const [historyItem, setHistoryItem] = useState<InventoryItem | null>(null);
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/inventory/items`);
      const data = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
      setItems(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch inventory');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLowStock = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/inventory/items/low-stock`);
      const data = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
      setLowStock(data);
    } catch {
      setLowStock([]);
    }
  }, []);

  const fetchExpiringSoon = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/inventory/items/expiring-soon`);
      const data = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
      setExpiringSoon(data);
    } catch {
      setExpiringSoon([]);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    fetchLowStock();
    fetchExpiringSoon();
  }, [fetchItems, fetchLowStock, fetchExpiringSoon]);

  const openTxDialog = (item: InventoryItem, type: 'receive' | 'issue' | 'adjust') => {
    setTxItem(item);
    setTxType(type);
    setTxQuantity('');
    setTxUnitCost(String(item.unitCost || ''));
    setTxReason('');
    setTxDialogOpen(true);
  };

  const handleTransaction = async () => {
    if (!txItem) return;
    setTxSubmitting(true);
    try {
      const qty = parseFloat(txQuantity);
      if (isNaN(qty) || qty <= 0) {
        setError('Quantity must be a positive number');
        setTxSubmitting(false);
        return;
      }

      if (txType === 'receive') {
        await axios.post(`${API_BASE}/inventory/items/${txItem.id}/receive`, {
          quantity: qty,
          unitCost: parseFloat(txUnitCost) || 0,
          userId: 'operator',
        });
      } else if (txType === 'issue') {
        await axios.post(`${API_BASE}/inventory/items/${txItem.id}/issue`, {
          quantity: qty,
          userId: 'operator',
        });
      } else {
        await axios.post(`${API_BASE}/inventory/items/${txItem.id}/adjust`, {
          quantity: qty,
          userId: 'operator',
          reason: txReason || 'Manual adjustment',
        });
      }

      setTxDialogOpen(false);
      fetchItems();
      fetchLowStock();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Transaction failed');
    } finally {
      setTxSubmitting(false);
    }
  };

  const openHistory = async (item: InventoryItem) => {
    setHistoryItem(item);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/inventory/items/${item.id}/transactions`);
      const data = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
      setTransactions(data);
    } catch {
      setTransactions([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const stockLevel = (item: InventoryItem) => {
    if (item.quantityOnHand <= 0) return 'error';
    if (item.quantityOnHand <= item.reorderPoint) return 'warning';
    return 'success';
  };

  const stockPercent = (item: InventoryItem) => {
    if (!item.reorderQuantity || item.reorderQuantity === 0) return 100;
    return Math.min(100, (item.quantityOnHand / (item.reorderQuantity + item.reorderPoint)) * 100);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Inventory Workflows
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage inventory items, receipts, issues, and adjustments in real time.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => { fetchItems(); fetchLowStock(); fetchExpiringSoon(); }}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Inventory2 color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Items</Typography>
                  <Typography variant="h5">{items.length}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingDown color="warning" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Low Stock Alerts</Typography>
                  <Typography variant="h5" color="warning.main">{lowStock.length}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Schedule color="error" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Expiring Soon</Typography>
                  <Typography variant="h5" color="error.main">{expiringSoon.length}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label="All Items" />
        <Tab label={`Low Stock (${lowStock.length})`} />
        <Tab label={`Expiring Soon (${expiringSoon.length})`} />
        {historyItem && <Tab label={`History: ${historyItem.itemCode}`} />}
      </Tabs>

      {/* All Items */}
      <TabPanel value={tab} index={0}>
        {loading && items.length === 0 ? (
          <Box>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={50} sx={{ mb: 1, borderRadius: 1 }} />
            ))}
          </Box>
        ) : items.length === 0 ? (
          <Alert severity="info">No inventory items found. Create items via the API or master data setup.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">On Hand</TableCell>
                  <TableCell>Stock Level</TableCell>
                  <TableCell align="right">Unit Cost</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{item.itemCode}</Typography>
                    </TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>
                      {item.category && <Chip label={item.category} size="small" variant="outlined" />}
                    </TableCell>
                    <TableCell align="right">
                      {item.quantityOnHand} {item.unit}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                        <LinearProgress
                          variant="determinate"
                          value={stockPercent(item)}
                          color={stockLevel(item) as any}
                          sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                        />
                        {item.quantityOnHand <= item.reorderPoint && (
                          <Warning fontSize="small" color="warning" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {item.unitCost != null ? `$${Number(item.unitCost).toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell>{item.location || '-'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Receive">
                        <IconButton size="small" color="success" onClick={() => openTxDialog(item, 'receive')}>
                          <Add />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Issue">
                        <IconButton size="small" color="primary" onClick={() => openTxDialog(item, 'issue')}>
                          <Remove />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Adjust">
                        <IconButton size="small" onClick={() => openTxDialog(item, 'adjust')}>
                          <SwapHoriz />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Transaction History">
                        <IconButton size="small" onClick={() => { openHistory(item); setTab(3); }}>
                          <Inventory2 />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Low Stock */}
      <TabPanel value={tab} index={1}>
        {lowStock.length === 0 ? (
          <Alert severity="success">All stock levels are above reorder points. No action needed.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'warning.50' }}>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">On Hand</TableCell>
                  <TableCell align="right">Reorder Point</TableCell>
                  <TableCell align="right">Reorder Qty</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lowStock.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{item.itemCode}</Typography>
                    </TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${item.quantityOnHand} ${item.unit}`}
                        color={item.quantityOnHand <= 0 ? 'error' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{item.reorderPoint} {item.unit}</TableCell>
                    <TableCell align="right">{item.reorderQuantity} {item.unit}</TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<Add />}
                        onClick={() => openTxDialog(item, 'receive')}
                      >
                        Receive
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Expiring Soon */}
      <TabPanel value={tab} index={2}>
        {expiringSoon.length === 0 ? (
          <Alert severity="success">No items expiring soon.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'error.50' }}>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Lot</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell align="right">On Hand</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expiringSoon.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{item.itemCode}</Typography>
                    </TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.lotNumber || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : '-'}
                        color="error"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{item.quantityOnHand} {item.unit}</TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => openTxDialog(item, 'issue')}
                      >
                        Issue / Use
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Transaction History */}
      {historyItem && (
        <TabPanel value={tab} index={3}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Transaction History for {historyItem.itemCode} - {historyItem.name}
          </Typography>
          {historyLoading ? (
            <Box>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Box>
          ) : transactions.length === 0 ? (
            <Alert severity="info">No transactions recorded for this item.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Unit Cost</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((tx) => (
                    <TableRow key={tx.id} hover>
                      <TableCell>
                        {new Date(tx.createdAt).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={tx.transactionType}
                          size="small"
                          color={
                            tx.transactionType === 'receive' ? 'success' :
                            tx.transactionType === 'issue' ? 'primary' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell align="right">{tx.quantity}</TableCell>
                      <TableCell align="right">
                        {tx.unitCost != null ? `$${Number(tx.unitCost).toFixed(2)}` : '-'}
                      </TableCell>
                      <TableCell>{tx.referenceType || '-'}</TableCell>
                      <TableCell>{tx.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      )}

      {/* Transaction Dialog */}
      <Dialog open={txDialogOpen} onClose={() => setTxDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {txType === 'receive' ? 'Receive Inventory' : txType === 'issue' ? 'Issue Inventory' : 'Adjust Inventory'}
          {txItem && ` - ${txItem.itemCode}`}
        </DialogTitle>
        <DialogContent>
          {txItem && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Current on hand: {txItem.quantityOnHand} {txItem.unit}
              </Typography>
              <TextField
                label="Quantity"
                type="number"
                fullWidth
                value={txQuantity}
                onChange={(e) => setTxQuantity(e.target.value)}
                sx={{ mb: 2 }}
                inputProps={{ min: 0, step: 'any' }}
                autoFocus
              />
              {txType === 'receive' && (
                <TextField
                  label="Unit Cost"
                  type="number"
                  fullWidth
                  value={txUnitCost}
                  onChange={(e) => setTxUnitCost(e.target.value)}
                  sx={{ mb: 2 }}
                  inputProps={{ min: 0, step: '0.01' }}
                />
              )}
              {txType === 'adjust' && (
                <TextField
                  label="Reason"
                  fullWidth
                  value={txReason}
                  onChange={(e) => setTxReason(e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder="e.g., Cycle count correction"
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTxDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTransaction}
            disabled={txSubmitting || !txQuantity}
            color={txType === 'receive' ? 'success' : txType === 'issue' ? 'primary' : 'warning'}
          >
            {txSubmitting ? 'Processing...' : txType === 'receive' ? 'Receive' : txType === 'issue' ? 'Issue' : 'Adjust'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
